import { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton, Skeleton } from '@mui/material';
import { ChevronLeft, ChevronRight, LocalOffer, TrendingUp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Skeleton loader for carousel
const VoucherCarouselSkeleton = () => {
    const { colors } = useSelector((state) => state.template);
    const primaryColor = colors?.primary || '#02b3ad';

    return (
        <Box
            sx={{
                width: '100%',
                background: '#fff',
                borderRadius: { xs: '16px', md: '20px' },
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)',
                overflow: 'hidden',
                position: 'relative',
                border: `2px solid ${primaryColor}10`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    padding: { xs: '18px 52px', sm: '20px 60px', md: '24px 70px' },
                    gap: { xs: 2, md: 2.5 },
                }}
            >
                {/* Left Section - Campaign Details Skeleton */}
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Skeleton variant="rounded" width={140} height={28} sx={{ marginBottom: 1.5, borderRadius: '20px' }} />
                    <Skeleton variant="text" width="80%" height={36} sx={{ marginBottom: 1 }} />
                    <Skeleton variant="text" width="60%" height={20} sx={{ marginBottom: 0.5 }} />
                    <Skeleton variant="text" width="50%" height={20} />
                </Box>

                {/* Center Section - Countdown Timer Skeleton */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: { xs: 1, sm: 1.5 },
                        alignItems: 'center',
                        justifyContent: { xs: 'flex-start', md: 'center' },
                    }}
                >
                    {[1, 2, 3, 4].map((item) => (
                        <Box key={item} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Skeleton variant="rounded" width={{ xs: 46, sm: 52, md: 58 }} height={{ xs: 44, sm: 50, md: 54 }} sx={{ borderRadius: '10px' }} />
                            <Skeleton variant="text" width={36} height={14} />
                        </Box>
                    ))}
                </Box>

                {/* Right Section - Button Skeleton */}
                <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <Skeleton variant="rounded" width={{ xs: '100%', md: 140 }} height={44} sx={{ borderRadius: '5px', marginBottom: 0.75 }} />
                    <Skeleton variant="text" width={100} height={14} sx={{ margin: '0 auto' }} />
                </Box>
            </Box>

            {/* Pagination dots skeleton */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    paddingBottom: 2,
                }}
            >
                {[1, 2, 3].map((dot) => (
                    <Skeleton key={dot} variant="circular" width={8} height={8} />
                ))}
            </Box>
        </Box>
    );
};

const VoucherCard = ({ voucher, showBuyButton }) => {
    const navigate = useNavigate();
    const { colors, typography, buttons } = useSelector((state) => state.template);

    // Use template colors with fallbacks to match existing theme
    const primaryColor = colors?.primary || '#02b3ad';
    const headingsColor = colors?.headings || '#343434';
    const bodyFont = typography?.body || 'Poppins';
    const headingsFont = typography?.headings || 'Poppins';
    const buttonPrimaryColor = buttons?.primary || primaryColor;
    const buttonRadius = buttons?.borderRadius || 5;

    const [timeRemaining, setTimeRemaining] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const calculateTime = () => {
            const now = new Date().getTime();
            const countDownDate = new Date(voucher?.endDate).getTime();
            const differenceTime = countDownDate - now;

            if (differenceTime <= 0) {
                setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                setIsExpired(true);
                return true; // expired
            } else {
                const days = Math.floor(differenceTime / (1000 * 60 * 60 * 24));
                const hours = Math.floor((differenceTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((differenceTime % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((differenceTime % (1000 * 60)) / 1000);

                setTimeRemaining({ days, hours, minutes, seconds });
                setIsExpired(false);
                return false; // not expired
            }
        };

        // Initial calculation
        const expired = calculateTime();
        if (expired) return;

        // Set up interval
        const timer = setInterval(() => {
            const expired = calculateTime();
            if (expired) {
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [voucher?.endDate]);

    // Calculate urgency level for visual feedback
    const totalHoursLeft = (timeRemaining.days * 24) + timeRemaining.hours;
    const isUrgent = totalHoursLeft < 24 && !isExpired;
    const isMediumUrgent = totalHoursLeft < 72 && totalHoursLeft >= 24;

    return (
        <Box
            sx={{
                background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 100%)`,
                borderRadius: { xs: '16px', md: '20px' },
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)',
                overflow: 'hidden',
                height: '100%',
                position: 'relative',
                border: `2px solid ${primaryColor}20`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    boxShadow: '0 12px 48px 0 rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-4px)',
                    borderColor: `${primaryColor}40`,
                },
            }}
        >
            {/* Decorative elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -30,
                    left: -30,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${primaryColor}10 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    padding: { xs: '18px 52px', sm: '20px 60px', md: '24px 70px' },
                    gap: { xs: 2, md: 2.5 },
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                {/* Left Section - Campaign Details */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Campaign Badge with icon */}
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            background: isExpired
                                ? 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)'
                                : isUrgent
                                ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                                : isMediumUrgent
                                ? 'linear-gradient(135deg, #ffd93d 0%, #ffb84d 100%)'
                                : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                            padding: '6px 12px',
                            borderRadius: '20px',
                            marginBottom: 1.5,
                            boxShadow: isExpired ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <LocalOffer sx={{ fontSize: '14px', color: '#fff' }} />
                        <Typography
                            sx={{
                                color: '#fff',
                                fontSize: { xs: '10px', sm: '11px' },
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                fontFamily: bodyFont,
                                textTransform: 'uppercase',
                            }}
                        >
                            {isExpired ? 'Expired' : isUrgent ? 'Ending Soon!' : 'Limited Time Offer'}
                        </Typography>
                    </Box>

                    {/* Campaign Name with enhanced styling */}
                    <Typography
                        variant="h4"
                        sx={{
                            color: headingsColor,
                            fontSize: { xs: '18px', sm: '20px', md: '24px' },
                            fontWeight: 800,
                            marginBottom: 1,
                            lineHeight: 1.2,
                            fontFamily: headingsFont,
                            background: `linear-gradient(135deg, ${headingsColor} 0%, ${headingsColor}cc 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}
                    >
                        {voucher?.name || voucher?.campaignName || 'Special Voucher Campaign'}
                    </Typography>

                    {/* Campaign Description with better contrast */}
                    <Typography
                        sx={{
                            color: '#5a5a5a',
                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                            marginBottom: { xs: 1.5, md: 0 },
                            lineHeight: 1.5,
                            maxWidth: '500px',
                            fontFamily: bodyFont,
                            fontWeight: 500,
                        }}
                    >
                        {voucher?.description || 'Grab exclusive eVouchers at amazing prices. Limited stock available!'}
                    </Typography>

                    {/* Add trending indicator for popular campaigns */}
                    {!isExpired && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <TrendingUp sx={{ fontSize: '14px', color: primaryColor }} />
                            <Typography
                                sx={{
                                    color: primaryColor,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    fontFamily: bodyFont,
                                }}
                            >
                                Trending Now
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Center Section - Enhanced Countdown Timer */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: { xs: 1, sm: 1.5 },
                        alignItems: 'center',
                        justifyContent: { xs: 'flex-start', md: 'center' },
                        flexWrap: 'wrap',
                    }}
                >
                    {[
                        { value: timeRemaining.days, label: 'Days', color: primaryColor },
                        { value: timeRemaining.hours, label: 'Hrs', color: primaryColor },
                        { value: timeRemaining.minutes, label: 'Mins', color: isUrgent ? '#ff6b6b' : primaryColor },
                        { value: timeRemaining.seconds, label: 'Secs', color: isUrgent ? '#ff6b6b' : primaryColor },
                    ].map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                                    borderRadius: '10px',
                                    padding: { xs: '8px 10px', sm: '10px 12px', md: '10px 14px' },
                                    minWidth: { xs: '46px', sm: '52px', md: '58px' },
                                    textAlign: 'center',
                                    boxShadow: '0 3px 12px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                                    border: '1px solid rgba(0, 0, 0, 0.05)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: `linear-gradient(90deg, transparent, ${item.color}, transparent)`,
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: item.color,
                                        fontSize: { xs: '20px', sm: '22px', md: '26px' },
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        fontFamily: 'monospace',
                                        letterSpacing: '-1px',
                                    }}
                                >
                                    {String(item.value).padStart(2, '0')}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: '#707070',
                                    fontSize: { xs: '9px', sm: '10px', md: '11px' },
                                    fontWeight: 600,
                                    fontFamily: bodyFont,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                }}
                            >
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Right Section - Enhanced CTA Button */}
                {showBuyButton && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'stretch', md: 'center' },
                            width: { xs: '100%', md: 'auto' },
                            gap: 0.75,
                        }}
                    >
                        <Button
                            onClick={() => navigate(`/evouchers`)}
                            disabled={isExpired}
                            variant="contained"
                            sx={{
                                background: isExpired
                                    ? 'linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)'
                                    : `linear-gradient(135deg, ${buttonPrimaryColor} 0%, ${buttonPrimaryColor}dd 100%)`,
                                color: isExpired ? '#999' : '#fff',
                                fontSize: { xs: '14px', md: '15px' },
                                fontWeight: 700,
                                borderRadius: `${buttonRadius}px`,
                                padding: { xs: '11px 32px', md: '12px 36px' },
                                textTransform: 'none',
                                width: { xs: '100%', md: 'auto' },
                                fontFamily: bodyFont,
                                boxShadow: isExpired
                                    ? 'none'
                                    : `0 6px 20px ${buttonPrimaryColor}40, inset 0 1px 0 rgba(255,255,255,0.2)`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                    transition: 'left 0.5s',
                                },
                                '&:hover': {
                                    background: isExpired
                                        ? 'linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)'
                                        : `linear-gradient(135deg, ${buttonPrimaryColor}dd 0%, ${buttonPrimaryColor} 100%)`,
                                    transform: isExpired ? 'none' : 'translateY(-2px) scale(1.02)',
                                    boxShadow: isExpired
                                        ? 'none'
                                        : `0 10px 28px ${buttonPrimaryColor}50, inset 0 1px 0 rgba(255,255,255,0.3)`,
                                    '&::before': {
                                        left: '100%',
                                    },
                                },
                                '&:active': {
                                    transform: isExpired ? 'none' : 'translateY(0) scale(0.98)',
                                },
                                '&:disabled': {
                                    cursor: 'not-allowed',
                                },
                            }}
                        >
                            {isExpired ? 'Campaign Ended' : 'Shop Now'}
                        </Button>

                        {!isExpired && (
                            <Typography
                                sx={{
                                    color: '#707070',
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    fontFamily: bodyFont,
                                    textAlign: 'center',
                                    fontStyle: 'italic',
                                }}
                            >
                                Don't miss out!
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const VoucherCarousel = ({ vouchers, showBuyButton = true, isLoading = false }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const { colors } = useSelector((state) => state.template);

    const primaryColor = colors?.primary || '#02b3ad';

    // Auto-play functionality
    useEffect(() => {
        if (!isAutoPlaying || vouchers.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % vouchers.length);
        }, 8000);

        return () => clearInterval(interval);
    }, [isAutoPlaying, vouchers.length]);

    const handlePrevious = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prevIndex) => (prevIndex - 1 + vouchers.length) % vouchers.length);
    };

    const handleNext = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % vouchers.length);
    };

    const handleDotClick = (index) => {
        setIsAutoPlaying(false);
        setCurrentIndex(index);
    };

    // Show skeleton while loading
    if (isLoading) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: { xs: '12px', sm: '16px', md: '20px' },
                }}
            >
                <VoucherCarouselSkeleton />
            </Box>
        );
    }

    if (!vouchers || vouchers.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '1400px',
                margin: '0 auto',
                padding: { xs: '12px', sm: '16px', md: '20px' },
            }}
        >
            {/* Carousel Container */}
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: { xs: '16px', md: '20px' },
                }}
            >
                {/* Slides */}
                <Box
                    sx={{
                        display: 'flex',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: `translateX(-${currentIndex * 100}%)`,
                    }}
                >
                    {vouchers.map((voucher, index) => (
                        <Box
                            key={voucher._id || index}
                            sx={{
                                minWidth: '100%',
                                width: '100%',
                            }}
                        >
                            <VoucherCard voucher={voucher} showBuyButton={showBuyButton} />
                        </Box>
                    ))}
                </Box>

                {/* Navigation Arrows - Enhanced design */}
                {vouchers.length > 1 && (
                    <>
                        <IconButton
                            onClick={handlePrevious}
                            sx={{
                                position: 'absolute',
                                left: { xs: '12px', sm: '20px' },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
                                color: primaryColor,
                                width: { xs: '36px', sm: '44px' },
                                height: { xs: '36px', sm: '44px' },
                                zIndex: 2,
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    background: `linear-gradient(145deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                    color: '#fff',
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: `0 6px 20px ${primaryColor}40`,
                                },
                            }}
                        >
                            <ChevronLeft sx={{ fontSize: { xs: '22px', sm: '26px' } }} />
                        </IconButton>

                        <IconButton
                            onClick={handleNext}
                            sx={{
                                position: 'absolute',
                                right: { xs: '12px', sm: '20px' },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
                                color: primaryColor,
                                width: { xs: '36px', sm: '44px' },
                                height: { xs: '36px', sm: '44px' },
                                zIndex: 2,
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    background: `linear-gradient(145deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                    color: '#fff',
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: `0 6px 20px ${primaryColor}40`,
                                },
                            }}
                        >
                            <ChevronRight sx={{ fontSize: { xs: '22px', sm: '26px' } }} />
                        </IconButton>
                    </>
                )}
            </Box>

            {/* Pagination Dots - Enhanced design */}
            {vouchers.length > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                        marginTop: { xs: 2, md: 2.5 },
                    }}
                >
                    {vouchers.map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => handleDotClick(index)}
                            sx={{
                                width: currentIndex === index ? '32px' : '10px',
                                height: '10px',
                                borderRadius: '5px',
                                background: currentIndex === index
                                    ? `linear-gradient(90deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
                                    : '#e0e0e0',
                                cursor: 'pointer',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: currentIndex === index ? `0 2px 8px ${primaryColor}40` : 'none',
                                '&:hover': {
                                    background: currentIndex === index
                                        ? `linear-gradient(90deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
                                        : '#c0c0c0',
                                    transform: 'scale(1.1)',
                                },
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default VoucherCarousel;
